import React, { useMemo, useState } from 'react';
import { RawDataRowProvenance, SessionRawRowsResponse } from '../data/api';
import { LinkEidPanel } from './LinkEidPanel';

// =============================================================================
// Raw ingest data viewer — "trace the raw data" behind an event.
//
// An event carries the `data_session` of the file it was built from; these
// components render every raw row of that file next to what ingest made of it
// (linked animal, link status, processing errors). That pairing is the point:
// a row that never linked is invisible in the event's animal count, and this
// is where you see why — e.g. deal A260006's slaughter row 34 sat at
// UNLINKED_NO_DEAL while the event reported one animal fewer than were killed.
//
// Shared by the Event history screen (AdminHome) and the deal overview's
// Audit trail tab so the two can't drift apart.
// =============================================================================

/** Upload timestamp for a revision, e.g. "28 Jul 2026, 14:04". */
export function formatRevisionTimestamp(value: string): string {
  const t = Date.parse(value);
  if (!t) return value;
  return new Date(t).toLocaleString('en-ZA', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

export function formatRawValue(value: any): string {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

// Ingest stamps each row with how it resolved against a deal. Anything other
// than LINKED means the row's animal is missing from the event's count.
const LINK_STATUS_LABELS: Record<string, string> = {
  LINKED: 'Linked',
  UNLINKED_NO_DEAL: 'No deal',
  UNLINKED_NO_IDENTIFIER: 'No identifier',
  UNLINKED_NO_ANIMAL: 'No animal',
  INVALID_EID: 'Invalid EID',
};

const linkStatusOf = (row: RawDataRowProvenance): string =>
  String((row.mapped_data || {}).deal_link_status || '');

const linkStatusLabel = (status: string): string =>
  LINK_STATUS_LABELS[status] || status.replace(/_/g, ' ').toLowerCase().replace(/^./, (c) => c.toUpperCase());

const isLinked = (row: RawDataRowProvenance): boolean => linkStatusOf(row) === 'LINKED';

/** The EID written in this row's own upload. */
const eidOf = (row: RawDataRowProvenance): string => {
  const mapped = (row.mapped_data || {}).eid;
  return mapped ? String(mapped) : '';
};

/**
 * A tag value the upload supplied that isn't a valid 15-digit microchip number
 * ("NOTAG", "-", "n/a"). Ingest drops it and creates the animal with a blank
 * EID rather than losing it, keeping the original here so the gap stays visible
 * and someone can link the real tag later.
 */
const invalidEidOf = (row: RawDataRowProvenance): string => {
  const value = (row.mapped_data || {}).invalid_eid;
  return value ? String(value) : '';
};

/**
 * Whether the row's animal is absent from its event's count. Two ways to get
 * there, and the UI must not conflate them with a clean link: the row never
 * linked at ingest, or it linked to an animal that has since been soft-deleted.
 * `deal_link_status` is frozen at ingest and cannot tell you the second.
 */
const isNotCounted = (row: RawDataRowProvenance): boolean =>
  Boolean(row.animal_deleted) || Boolean(linkStatusOf(row) && !isLinked(row));

function LinkStatusPill({ row }: { row: RawDataRowProvenance }) {
  const status = linkStatusOf(row);
  // A deleted animal wins over the stamp: the row reads LINKED forever, but the
  // animal is out of the system until it is restored.
  if (row.animal_deleted) {
    return (
      <span
        className="inline-flex whitespace-nowrap rounded-full border border-[#e7bbb9] bg-[#f8eae7] px-2 py-0.5 text-[11px] font-semibold text-[#6b1010]"
        title={`Row linked to a deleted animal${status ? ` (uploaded as ${status})` : ''} — excluded from every count until restored`}
      >
        Deleted
      </span>
    );
  }
  if (!status) return <span className="text-avcs-muted">—</span>;
  const ok = status === 'LINKED';
  return (
    <span
      className={`inline-flex whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-semibold ${
        ok ? 'border-[#b7dcc0] bg-[#eaf7ee] text-[#1c6b32]' : 'border-[#e8c9a6] bg-[#fdf3e7] text-[#8a4b12]'
      }`}
      title={status}
    >
      {linkStatusLabel(status)}
    </span>
  );
}

/**
 * All raw rows of one uploaded file as a spreadsheet-style table. Columns are
 * the union of `raw_data` keys, prefixed by what ingest resolved for the row.
 * Rows that did not link are tinted so they stand out from the linked bulk.
 */
export const RawRowsTable = React.memo(function RawRowsTable({
  rows,
  onLinkRow,
}: {
  rows: RawDataRowProvenance[];
  /** When given, rows missing from the event's count offer a "Link" action. */
  onLinkRow?: (row: RawDataRowProvenance) => void;
}) {
  const columns = useMemo(() => {
    const set = new Set<string>();
    rows.forEach((r) => Object.keys(r.raw_data || {}).forEach((k) => set.add(k)));
    return Array.from(set);
  }, [rows]);

  // Only worth a column when something actually failed.
  const showErrors = useMemo(() => rows.some((r) => r.processing_errors), [rows]);
  const showStatus = useMemo(() => rows.some((r) => linkStatusOf(r) || r.animal_deleted), [rows]);

  if (rows.length === 0) return <p className="text-sm text-avcs-muted">No raw rows found for this file.</p>;

  const th = 'whitespace-nowrap border-b border-[#1e6bb824] px-2 py-1.5 text-left font-semibold uppercase tracking-wide text-avcs-muted';
  return (
    <div className="max-h-[60vh] overflow-auto rounded-lg border border-[#1e6bb824]">
      <table className="min-w-full text-xs">
        <thead className="sticky top-0 bg-[#f8fbff]">
          <tr>
            {onLinkRow && <th className={th}><span className="sr-only">Actions</span></th>}
            <th className={th}>Row</th>
            {showStatus && <th className={th}>Linked</th>}
            <th className={th} title="The animal this row resolves to today, which is not always the EID the row was uploaded with">Animal now</th>
            {showErrors && <th className={th}>Error</th>}
            {columns.map((c) => (
              <th key={c} className={th}>{c}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-[#1e6bb812]">
          {rows.map((r) => {
            const rowEid = eidOf(r);
            const liveEid = r.animal_eid ? String(r.animal_eid) : '';
            // The row was uploaded under one EID but its animal now carries
            // another — a re-tag, or a farm placeholder the abattoir read
            // differently. Worth showing both; it is otherwise invisible.
            const retagged = Boolean(liveEid && rowEid && liveEid !== rowEid);
            const badEid = invalidEidOf(r);
            return (
              <tr key={r.id} className={isNotCounted(r) ? 'bg-[#fffaf3]' : 'bg-white'}>
                {onLinkRow && (
                  <td className="whitespace-nowrap px-2 py-1.5">
                    {/* Only rows carrying an EID that never made it into the
                        count can be linked — there is nothing to assign otherwise. */}
                    {isNotCounted(r) && rowEid ? (
                      <button
                        type="button"
                        className="rounded-md border border-[#1e6bb833] px-2 py-0.5 text-[11px] font-semibold text-[#1e6bb8] hover:bg-[#eef5ff]"
                        onClick={() => onLinkRow(r)}
                      >
                        Link
                      </button>
                    ) : null}
                  </td>
                )}
                <td className="whitespace-nowrap px-2 py-1.5 tabular-nums text-avcs-muted">{r.row_number ?? '—'}</td>
                {showStatus && (
                  <td className="whitespace-nowrap px-2 py-1.5"><LinkStatusPill row={r} /></td>
                )}
                <td className="whitespace-nowrap px-2 py-1.5 font-mono text-avcs-text" title={r.animal_id || 'No animal record'}>
                  {liveEid || rowEid || (r.animal_id ? 'linked' : '—')}
                  {retagged && (
                    <span className="ml-1 font-sans text-[11px] text-[#8a4b12]" title={`This row was uploaded as ${rowEid}`}>
                      (was {rowEid})
                    </span>
                  )}
                  {!liveEid && badEid && (
                    <span
                      className="ml-1 font-sans text-[11px] text-[#8a4b12]"
                      title={`Uploaded as "${badEid}", which is not a valid 15-digit EID. The animal was created without a tag and can be linked to its real EID later.`}
                    >
                      no tag (“{badEid}”)
                    </span>
                  )}
                </td>
                {showErrors && (
                  <td className="px-2 py-1.5 text-[#6b1010]" title={r.processing_errors || ''}>
                    {r.processing_errors || '—'}
                  </td>
                )}
                {columns.map((c) => (
                  <td
                    key={c}
                    className="whitespace-nowrap px-2 py-1.5 text-avcs-text"
                    title={formatRawValue((r.raw_data || {})[c])}
                  >
                    {formatRawValue((r.raw_data || {})[c])}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
});

export interface RawDataModalState {
  loading: boolean;
  error: string;
  /** Original upload filename, e.g. "20260728-1631201.csv". */
  filename: string;
  /** Direct link to the stored file, already normalised to the media host. */
  fileUrl?: string;
  /** Human label for the event the file was uploaded for. */
  eventLabel: string;
  session: SessionRawRowsResponse['session'];
  rows: RawDataRowProvenance[];
  count: number;
  limit: number;
  /**
   * Which revision of the file these rows are. Present whenever the file has
   * been revised, so the viewer can say so rather than presenting a superseded
   * upload as the data. `null` for an unrevised file (nothing to disclose).
   */
  revision?: {
    number: number;
    count: number;
    isCurrent: boolean;
    uploadedAt?: string | null;
    /** One-line description of what the current revision changed. */
    diffSummary?: string;
  } | null;
}

/**
 * Modal wrapper around `RawRowsTable`: the source file (downloadable), a
 * search box, and an unlinked-only filter for tracing rows that never made it
 * into the event.
 */
export function RawDataModal({
  state,
  onClose,
  onReload,
}: {
  state: RawDataModalState;
  onClose: () => void;
  /** Re-fetch the file after a link so the row and counts reflect the change. */
  onReload?: () => void;
}) {
  const [search, setSearch] = useState('');
  const [unlinkedOnly, setUnlinkedOnly] = useState(false);
  // Linking replaces the table in place rather than stacking a second modal:
  // one dialog, one escape route (see the Back control below).
  const [linkingRow, setLinkingRow] = useState<RawDataRowProvenance | null>(null);
  const [linkedMessage, setLinkedMessage] = useState('');

  const unlinkedCount = useMemo(() => state.rows.filter(isNotCounted).length, [state.rows]);

  const visibleRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    let rows = state.rows;
    if (unlinkedOnly) rows = rows.filter(isNotCounted);
    if (!q) return rows;
    return rows.filter((r) =>
      [
        r.row_number,
        eidOf(r),
        invalidEidOf(r),
        r.animal_eid,
        linkStatusOf(r),
        r.animal_deleted ? 'deleted' : '',
        r.animal_id,
        r.processing_errors,
        ...Object.values(r.raw_data || {}),
      ]
        .filter((v) => v !== null && v !== undefined && v !== '')
        .join(' ')
        .toLowerCase()
        .includes(q),
    );
  }, [state.rows, search, unlinkedOnly]);

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-[#0b223a66] px-4 py-8"
      onClick={onClose}
    >
      <div
        className="flex max-h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-[#1e6bb824] bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 border-b border-[#1e6bb824] px-4 py-3">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-avcs-text">Raw uploaded data</h3>
            <p className="truncate text-sm text-avcs-muted">
              {state.eventLabel}
              {state.filename ? <> · <span className="font-mono text-avcs-text">{state.filename}</span></> : ''}
              {state.session?.deal_no ? ` · ${state.session.deal_no}` : ''}
            </p>
            {state.revision && state.revision.count > 1 && (
              <p className="mt-0.5 text-xs">
                <span
                  className={
                    state.revision.isCurrent
                      ? 'rounded-full border border-[#b7dcc0] bg-[#eaf7ee] px-2 py-0.5 font-semibold text-[#1c6b32]'
                      : 'rounded-full border border-[#e8c9a6] bg-[#fdf3e7] px-2 py-0.5 font-semibold text-[#8a4b12]'
                  }
                >
                  Revision {state.revision.number} of {state.revision.count}
                  {state.revision.isCurrent ? '' : ' · superseded'}
                </span>
                {state.revision.uploadedAt && (
                  <span className="ml-2 text-avcs-muted">uploaded {formatRevisionTimestamp(state.revision.uploadedAt)}</span>
                )}
              </p>
            )}
          </div>
          <div className="flex shrink-0 items-center gap-3">
            {state.fileUrl && (
              <a
                className="whitespace-nowrap text-sm font-semibold text-[#1e6bb8] hover:underline"
                href={state.fileUrl}
                target="_blank"
                rel="noreferrer"
              >
                Download file
              </a>
            )}
            <button
              type="button"
              className="text-2xl leading-none text-avcs-muted hover:text-[#6b1010]"
              onClick={onClose}
              aria-label="Close"
            >×</button>
          </div>
        </div>
        <div className="overflow-auto p-4">
          {linkingRow ? (
            <>
              <button
                type="button"
                className="mb-3 text-sm font-semibold text-[#1e6bb8] hover:underline"
                onClick={() => setLinkingRow(null)}
              >
                ← Back to file
              </button>
              <LinkEidPanel
                row={linkingRow}
                onCancel={() => setLinkingRow(null)}
                onLinked={() => {
                  setLinkedMessage(
                    `Row ${linkingRow.row_number ?? ''} linked. The event's animal count has been updated.`.trim(),
                  );
                  setLinkingRow(null);
                  onReload?.();
                }}
              />
            </>
          ) : state.loading ? (
            <p className="text-sm text-avcs-muted">Loading raw data…</p>
          ) : state.error ? (
            <p className="rounded-lg border border-[#e7bbb9] bg-[#f8eae7] px-3 py-2 text-sm text-[#6b1010]">{state.error}</p>
          ) : (
            <>
              {linkedMessage && (
                <p
                  role="status"
                  className="mb-2 rounded-lg border border-[#b7dcc0] bg-[#eaf7ee] px-3 py-2 text-sm text-[#1c6b32]"
                >
                  {linkedMessage}
                </p>
              )}
              <div className="mb-2 flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <p className="text-xs text-avcs-muted">
                  All {state.count} raw row{state.count === 1 ? '' : 's'} uploaded in this file.
                  {state.count > state.rows.length && ` Showing the first ${state.rows.length} (limit ${state.limit}).`}
                  {search.trim() || unlinkedOnly ? ` Showing ${visibleRows.length}.` : ''}
                </p>
                <div className="flex items-center gap-2">
                  {unlinkedCount > 0 && (
                    <button
                      type="button"
                      className={`whitespace-nowrap rounded-lg border px-2 py-1 text-xs font-semibold ${
                        unlinkedOnly
                          ? 'border-[#8a4b12] bg-[#8a4b12] text-white'
                          : 'border-[#e8c9a6] bg-[#fdf3e7] text-[#8a4b12] hover:bg-[#fbead4]'
                      }`}
                      onClick={() => setUnlinkedOnly((v) => !v)}
                      title="Rows whose animal is missing from this event's count — either it never linked at upload, or the animal has since been deleted"
                    >
                      {unlinkedCount} not counted
                    </button>
                  )}
                  <input
                    className="rounded-lg border border-[#1e6bb833] px-2 py-1 text-xs md:w-64"
                    placeholder="Search rows (EID, value, status)…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                  />
                </div>
              </div>
              {state.revision && !state.revision.isCurrent && (
                // Read-only on purpose: these rows are history. Linking an EID
                // on a superseded row would attach an animal to data the system
                // no longer derives its counts from, so the row actions are
                // withheld here (see `onLinkRow` below) rather than disabled.
                <div className="mb-3 rounded-lg border border-[#e8c9a6] bg-[#fdf3e7] px-3 py-2 text-sm text-[#8a4b12]">
                  <span className="font-semibold">Superseded revision — read only.</span>{' '}
                  Revision {state.revision.count} is the one the deal&rsquo;s figures come from.
                  {state.revision.diffSummary ? ` Since this one: ${state.revision.diffSummary}.` : ''}
                </div>
              )}
              <RawRowsTable
                rows={visibleRows}
                onLinkRow={onReload && state.revision?.isCurrent !== false ? setLinkingRow : undefined}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
}
